import {
  Activity,
  Anchor,
  Award,
  Banknote,
  BarChart2,
  Bitcoin,
  Briefcase,
  Building2,
  CircleDollarSign,
  Coins,
  Cpu,
  Crown,
  DollarSign,
  Factory,
  FlaskConical,
  Gem,
  Globe,
  Home,
  Hotel,
  Landmark,
  Layers,
  Leaf,
  LineChart,
  MapPin,
  Package,
  Percent,
  Pickaxe,
  PieChart,
  Pill,
  Plane,
  Rocket,
  Scale,
  Shield,
  Ship,
  Sparkles,
  Sun,
  TreePine,
  TrendingUp,
  Truck,
  Wallet,
  Wheat,
  Zap,
} from 'lucide-react';

/** Fallback when `iconKey` is missing (legacy rows). */
export const LEGACY_PLAN_NAME_TO_ICON_KEY = {
  Mining: 'cpu',
  Bullion: 'bar-chart-2',
  Pharma: 'flask-conical',
};

export const PLAN_ICON_REGISTRY = {
  activity: Activity,
  anchor: Anchor,
  award: Award,
  banknote: Banknote,
  'bar-chart-2': BarChart2,
  bitcoin: Bitcoin,
  briefcase: Briefcase,
  'building-2': Building2,
  'circle-dollar-sign': CircleDollarSign,
  coins: Coins,
  cpu: Cpu,
  crown: Crown,
  'dollar-sign': DollarSign,
  factory: Factory,
  'flask-conical': FlaskConical,
  gem: Gem,
  globe: Globe,
  home: Home,
  hotel: Hotel,
  landmark: Landmark,
  layers: Layers,
  leaf: Leaf,
  'line-chart': LineChart,
  'map-pin': MapPin,
  package: Package,
  percent: Percent,
  pickaxe: Pickaxe,
  'pie-chart': PieChart,
  pill: Pill,
  plane: Plane,
  rocket: Rocket,
  scale: Scale,
  shield: Shield,
  ship: Ship,
  sparkles: Sparkles,
  sun: Sun,
  'tree-pine': TreePine,
  'trending-up': TrendingUp,
  truck: Truck,
  wallet: Wallet,
  wheat: Wheat,
  zap: Zap,
};

export const DEFAULT_PLAN_ICON_KEY = 'activity';

const PLAN_ICON_OPTIONS_UNSORTED = [
  { key: 'activity', label: 'Activity / pulse', keywords: 'pulse chart generic' },
  { key: 'cpu', label: 'CPU / Mining', keywords: 'mining rig gpu server bitcoin crypto hash' },
  { key: 'pickaxe', label: 'Pickaxe / Mining', keywords: 'mining dig ore excavation' },
  { key: 'gem', label: 'Gem / precious', keywords: 'bullion gold silver jewel precious stone' },
  { key: 'bar-chart-2', label: 'Bar chart', keywords: 'bullion trading stats analytics performance' },
  { key: 'line-chart', label: 'Line chart', keywords: 'trend growth stocks market graph' },
  { key: 'pie-chart', label: 'Pie chart', keywords: 'allocation portfolio share breakdown' },
  { key: 'flask-conical', label: 'Flask / Pharma', keywords: 'pharma science lab biotech chemistry research' },
  { key: 'pill', label: 'Pill / health', keywords: 'pharma medicine drug health medical' },
  { key: 'building-2', label: 'Building / real estate', keywords: 'real estate property office tower bidding' },
  { key: 'home', label: 'Home / housing', keywords: 'real estate house residential mortgage' },
  { key: 'landmark', label: 'Landmark / bank', keywords: 'bank finance institution government civic' },
  { key: 'hotel', label: 'Hotel / hospitality', keywords: 'hospitality lodging tourism' },
  { key: 'factory', label: 'Factory / industry', keywords: 'industrial manufacturing production' },
  { key: 'truck', label: 'Truck / logistics', keywords: 'shipping logistics freight delivery' },
  { key: 'plane', label: 'Plane / travel', keywords: 'aviation travel airline' },
  { key: 'ship', label: 'Ship / maritime', keywords: 'shipping cargo maritime' },
  { key: 'anchor', label: 'Anchor / maritime', keywords: 'marine port stability' },
  { key: 'bitcoin', label: 'Bitcoin', keywords: 'crypto btc blockchain' },
  { key: 'coins', label: 'Coins', keywords: 'crypto currency money token' },
  { key: 'wallet', label: 'Wallet', keywords: 'funds balance crypto storage' },
  { key: 'banknote', label: 'Banknote / cash', keywords: 'cash fiat money paper' },
  { key: 'dollar-sign', label: 'Dollar sign', keywords: 'usd money price' },
  { key: 'circle-dollar-sign', label: 'Dollar circle', keywords: 'money usd investment return' },
  { key: 'percent', label: 'Percent', keywords: 'rate yield interest apy' },
  { key: 'scale', label: 'Scale / legal', keywords: 'balance law compliance risk' },
  { key: 'shield', label: 'Shield / protection', keywords: 'security insurance stop loss safe' },
  { key: 'briefcase', label: 'Briefcase / business', keywords: 'corporate business work portfolio' },
  { key: 'trending-up', label: 'Trending up', keywords: 'growth profit gains bull market' },
  { key: 'rocket', label: 'Rocket / growth', keywords: 'launch startup moon high growth' },
  { key: 'sparkles', label: 'Sparkles / premium', keywords: 'premium vip bonus special' },
  { key: 'award', label: 'Award / rank', keywords: 'ranking tier achievement badge' },
  { key: 'crown', label: 'Crown / VIP', keywords: 'vip elite top tier royalty' },
  { key: 'globe', label: 'Globe / worldwide', keywords: 'global international world' },
  { key: 'map-pin', label: 'Map pin / location', keywords: 'region geography local' },
  { key: 'package', label: 'Package / delivery', keywords: 'parcel logistics product' },
  { key: 'layers', label: 'Layers / stack', keywords: 'stack diversified multi tier' },
  { key: 'leaf', label: 'Leaf / ESG', keywords: 'green esg sustainable nature' },
  { key: 'tree-pine', label: 'Tree / long-term', keywords: 'forest growth evergreen hold' },
  { key: 'wheat', label: 'Wheat / commodities', keywords: 'agriculture grain soft commodity' },
  { key: 'zap', label: 'Zap / energy', keywords: 'energy power speed electricity' },
  { key: 'sun', label: 'Sun / outlook', keywords: 'bright optimism solar' },
];

export const PLAN_ICON_OPTIONS = [...PLAN_ICON_OPTIONS_UNSORTED].sort((a, b) =>
  a.label.localeCompare(b.label)
);

export function isValidPlanIconKey(key) {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(PLAN_ICON_REGISTRY, key);
}

export function normalizePlanIconKey(raw) {
  if (raw == null) return '';
  const k = String(raw).trim().toLowerCase().replace(/\s+/g, '-');
  return isValidPlanIconKey(k) ? k : '';
}

export function effectivePlanIconKey(iconKey, planName) {
  const n = normalizePlanIconKey(iconKey);
  if (n) return n;
  const legacy = planName ? LEGACY_PLAN_NAME_TO_ICON_KEY[String(planName).trim()] : '';
  if (legacy && isValidPlanIconKey(legacy)) return legacy;
  return DEFAULT_PLAN_ICON_KEY;
}

/**
 * @param {string} [iconKey] — persisted key from DB
 * @param {string} [planName] — used for legacy rows without iconKey
 */
export function resolvePlanIconComponent(iconKey, planName) {
  const k = effectivePlanIconKey(iconKey, planName);
  return PLAN_ICON_REGISTRY[k] || PLAN_ICON_REGISTRY[DEFAULT_PLAN_ICON_KEY];
}

export function filterPlanIconOptions(search) {
  const q = String(search || '')
    .trim()
    .toLowerCase();
  if (!q) return PLAN_ICON_OPTIONS;
  return PLAN_ICON_OPTIONS.filter((o) => {
    const blob = `${o.key} ${o.label} ${o.keywords || ''}`.toLowerCase();
    return blob.includes(q);
  });
}
